import {
    Address,
    AuthContext,
    BaseButton,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderMailCartsRequest,
    ProviderMailCart,
    ProviderMailCartShipment,
    ProviderMailCartSortType,
    renderAddressGroup,
    renderString,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { CellProps, Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import { InboxMail } from '../inbox/inbox';
import * as S from './shipment.styles';

interface IMailCartProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IMailCarttate {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderMailCart>[] = [
    {
        id: 'Sender',
        Header: 'Sender',
        accessor: (row) => row.shipment?.from,
        show: false,
    },
    {
        id: 'Recipient',
        Header: 'Recipient',
        accessor: (row) => row.shipment?.to,
        Cell: ({ cell }) => {
            return cell.value ? renderAddressGroup([cell.value]) : null;
        },
    },
    {
        id: 'Method',
        Header: 'Method',
        accessor: (row) => row.shipment?.method,
        Cell: ({ cell }) => {
            const shipmentMethod = cell.value;
            return renderString(_.join(_.compact([shipmentMethod?.name, shipmentMethod?.estimate]), ', '));
        },
    },
    {
        id: 'Weight',
        Header: 'Weight',
        accessor: (row) => row.shipment?.weight,
        Cell: ({ cell }): string => (cell.value ? `${cell.value} oz.` : ''),
    },
    {
        id: 'Created',
        Header: 'Created',
        accessor: 'created',
        sortType: ProviderMailCartSortType.CREATED,
        defaultSort: { id: 'Created', desc: true },
        Cell: DataTableCell('DateTime'),
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderMailCartsRequest>[] = [
    {
        label: 'Cart Name',
        filterId: 'filterName',
        columnId: 'cartName',
        filterType: 'String',
    },
    {
        label: 'Created Date',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
];

class MailCartPage extends React.Component<IMailCartProps, IMailCarttate> {
    private presenter: DataTablePresenter<ProviderMailCart>;

    constructor(props: IMailCartProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderMailCart>({
            name: 'MailCart',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'Provider Dashboard - MailCart';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('mailcart');
    }

    public render(): React.ReactElement {
        return (
            <DataTable
                presenter={this.presenter}
                isLoading={this.state.loading}
                onFetch={this.onFetch}
                onRowClick={this.onRowClick}
                renderRowSubComponent={this.renderRowSubComponent}
            />
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (!_.isEqual(this.state.options, fetchOptions)) {
            try {
                this.setState({
                    options: fetchOptions,
                    loading: true,
                });
                this.authContext.cancelGroup('mailcart');
                this.authContext.setGroup('mailcart');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const mailCartListing = await this.props.rootStore.claimStore.loadCartListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                this.presenter.onDataUpdate(mailCartListing.items || []);
                this.presenter.onDataTotalChange(mailCartListing.total || 0);
                this.setState({
                    loading: false,
                });
            } catch (err) {
                this.dialogContext.showError(err);
            }
        }
    };

    private onRowClick = async (row: Row<ProviderMailCart>): Promise<void> => {
        // retrieve MailCart cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderMailCart>): React.ReactElement => {
        const cart = row.original;

        return (
            <S.Expand>
                <S.ExpandTable>
                    <S.ExpandTBody>
                        {cart.shipment?.tracking?.number && (
                            <S.ExpandTR>
                                <S.ExpandTH>Tracking Number:</S.ExpandTH>
                                <S.ExpandTD>{cart.shipment.tracking.number}</S.ExpandTD>
                            </S.ExpandTR>
                        )}
                    </S.ExpandTBody>
                </S.ExpandTable>
                {cart.shipment?.tracking?.url && (
                    <a href={cart.shipment.tracking.url} target="_blank" rel="noopener noreferrer">
                        <BaseButton variant="outlined">Open tracking website</BaseButton>
                    </a>
                )}
                <InboxMail mails={cart.items || []} hideShipmentInformation={true} />
            </S.Expand>
        );
    };
}

export const Shipment: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            return <MailCartPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
        },
    ),
);
